/**
 * Générateur — Automatisme : addition et soustraction de fractions de
 * dénominateurs quelconques mais simples.
 * Attendu ID : 1097 | Niveau : 4ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 652 :
 * « Addition et soustraction de fractions de dénominateurs quelconques mais
 * simples. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * EXTENSION de 1054 (5e). Les quatre cas de 5e (même dénominateur, entier,
 * multiples, premiers entre eux) restent en entretien ; le cas NEUF est
 * « quelconque » : aucun dénominateur n'est multiple de l'autre et le commun
 * n'est pas leur produit (5/6 + 3/4 → 12 ; 1/6 + 3/10 → 15 ; 5/6 − 4/9 → 18),
 * exactement ce que 1054 excluait (« jamais de dénominateur commun à
 * chercher »). « Quelconques » lève la restriction, « mais simples » borne
 * les nombres. Le geste neuf : trouver un nombre des deux tables.
 * Frontières : pas de simplification du résultat (objectif l. 660 ;
 * irréductible par construction comme 1054), pas de numérateur négatif
 * (l. 661-662), deux termes, pas de problème (l. 669).
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NC7 retient
 * les options de 6e, 5NC8 « et simplifier », « numérateurs relatifs » :
 * rejetés (objectifs) ; aucune option « quelconque simple », le cas neuf
 * vient du programme.
 *
 * ── Ce qui est RÉUTILISÉ de gen_1054 (exports, zéro duplication) ─────────
 * Les tirages des cas de 5e (tirerItem), le garde-fou d'item (résultat
 * entier, 1..50, premier avec le commun, hors sacrifiés de 1054), les
 * écritures (fraction à deux cases, phrase à deux lignes, exprTex), les
 * distracteurs des cas de 5e (tousCandidats, distracteursQcm à rang
 * pondéré), les affirmations du Vrai/Faux, les explications. Le cas
 * quelconque ajoute ses propres tirage et candidats ici.
 *
 * ── Plan de cartes (mêmes labels et mêmes clés que 1054, mêmes solveurs) ──
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre égalités sur
 *     deux lignes, saisie fraction à deux cases (strict) : multiples,
 *     premiers, quelconque en addition, quelconque en soustraction (les cas
 *     même dénominateur et entier, sans conversion à trouver, vivent au
 *     Vrai/Faux : choix mode auto, À ARBITRER).
 *   Carte 2 « Le même dénominateur d'abord » : QCM 4 choix (quelconque une
 *     fois sur deux, sinon multiples ou premiers ; bas_additionnes toujours,
 *     puis deux familles distinctes), Vrai/Faux 3 égalités dont au moins une
 *     quelconque.
 *
 * INVARIANTS du cas quelconque : couples de dénominateurs (4, 6) → 12,
 * (6, 9) → 18, (4, 10) → 20, (6, 8) → 24, (8, 12) → 24, (6, 10) → 30
 * (plafond du commun 30 ; (9, 12) → 36 écarté : choix mode auto, À
 * ARBITRER) ; fractions < 1 irréductibles ; résultat > 0, numérateur ≤ 50,
 * premier avec le commun (une seule écriture juste, jamais entier). Le
 * produit des dénominateurs (20/24 pour 10/12) n'est PAS un distracteur :
 * même valeur que la clé, l'automatisme est de voir 12.
 * SACRIFIÉS : les quatre de 1054 (importés, par item()) et 5/6 + 3/4 sous
 * toutes ses formes (5/6 et 3/4 jamais ensemble : 5/6 − 3/4, 3/4 + 5/6…),
 * exemple de la page neuve de la fiche (auto-testé).
 *
 * ── Distracteurs du cas quelconque (familles nommées) ────────────────────
 *   bas_additionnes          : (x ± y)/(b + d), PRINCIPALE (Eduscol 5e 2025
 *                              q.27 : 3/7 + 3/7 → 6/14) ;
 *   hauts_et_bas_multiplies  : (x × y)/(b × d), en addition (q.27 → 9/49) ;
 *   pas_converti             : le bon commun, un ou deux numérateurs pris
 *                              tels quels (nom de 1054) ;
 *   grand_denominateur       : NEUF, le plus grand dénominateur pris pour
 *                              commun (5/6 + 3/4 → 8/6) ;
 *   voisin_meme_table        : une conversion avec un fait de table voisin.
 */
import {
    randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';
import {
    tirerItem, item, fractionSous1, REPLIS as REPLIS_1054,
    exprTex, resTex, memeValeur, EXPLICATIONS as EXPLICATIONS_1054,
    distracteursQcm, affirmation, phraseFlash,
} from '../../5eme/nombres-rationnels/gen_1054';

const ATTENDU_ID = 1097;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_MEME = 'Le même dénominateur d\'abord';

// ─── Le cas quelconque ────────────────────────────────────────────────────

/** [b, d, commun] : ni multiple l'un de l'autre, commun < b × d, commun ≤ 30. */
export const COUPLES_QUELCONQUES = [[4, 6, 12], [6, 9, 18], [4, 10, 20], [6, 8, 24], [8, 12, 24], [6, 10, 30]];

/** L'exemple de la page neuve de la fiche : 5/6 et 3/4 ne sortent jamais ensemble. */
export const SACRIFIE = [{ a: 5, b: 6 }, { a: 3, b: 4 }];
const memeFraction = (f, g) => f.a === g.a && f.b === g.b;
const estSacrifie = (g, d) => (memeFraction(g, SACRIFIE[0]) && memeFraction(d, SACRIFIE[1])) || (memeFraction(g, SACRIFIE[1]) && memeFraction(d, SACRIFIE[0]));

const CAS_5E = ['meme_den', 'entier', 'multiples', 'premiers'];

/** Un item quelconque : { cas: 'quelconque', op, g, d, den, res } ; null si 300 essais échouent. */
export const tirerQuelconque = ({ opForce = null } = {}) => {
    for (let essai = 0; essai < 300; essai++) {
        const op = opForce || randomChoice(['+', '-']);
        const [b, d, commun] = randomChoice(COUPLES_QUELCONQUES);
        const [g, dr] = shuffle([fractionSous1(b), fractionSous1(d)]);
        if (estSacrifie(g, dr)) continue;
        const it = item('quelconque', op, g, dr, commun);
        if (it) return it;
    }
    return null;
};

/** Un item de n'importe quel cas (5e par 1054, quelconque ici). */
const tirer = (cas, contraintes = {}) => (cas === 'quelconque' ? tirerQuelconque(contraintes) : tirerItem(cas, contraintes));

/** Replis déterministes vérifiés, hors sacrifiés : ceux de 1054, plus 1/4 + 1/6 = 5/12 et 5/6 − 1/4 = 7/12. */
const REPLIS = {
    ...REPLIS_1054,
    quelconque_plus: item('quelconque', '+', { a: 1, b: 4 }, { a: 1, b: 6 }, 12),
    quelconque_moins: item('quelconque', '-', { a: 5, b: 6 }, { a: 1, b: 4 }, 12),
};

export const EXPLICATIONS = {
    ...EXPLICATIONS_1054,
    grand_denominateur: 'Le plus grand dénominateur ne suffit pas toujours : il faut un nombre qui soit dans les DEUX tables, 12 pour 6 et 4.',
    hauts_et_bas_multiplies: 'On ne multiplie pas : des parts de la même taille d\'abord, puis on ajoute les numérateurs.',
    regle: 'Des parts de la même taille d\'abord : je cherche un nombre dans les deux tables, puis j\'ajoute ou je retire les numérateurs.',
};

/** Les candidats d'un item quelconque, chacun avec sa valeur exacte, positifs, jamais la clé, jamais deux fois la même valeur. */
const candidatsQuelconque = (it) => {
    const { g, d, op, den, res } = it;
    const s = op === '+' ? 1 : -1;
    const kg = den / g.b;
    const kd = den / d.b;
    const c = [];
    const pousser = (code, num, dd) => { if (num >= 1 && dd >= 1) c.push({ code, val: { num, den: dd } }); };
    pousser('bas_additionnes', g.a + s * d.a, g.b + d.b);
    if (op === '+') pousser('hauts_et_bas_multiplies', g.a * d.a, g.b * d.b);
    pousser('pas_converti', g.a + s * d.a, den);
    pousser('pas_converti', g.a * kg + s * d.a, den);
    pousser('pas_converti', g.a + s * d.a * kd, den);
    pousser('grand_denominateur', g.a + s * d.a, Math.max(g.b, d.b));
    for (const dv of [-1, 1]) {
        pousser('voisin_meme_table', g.a * (kg + dv) + s * d.a * kd, den);
        pousser('voisin_meme_table', g.a * kg + s * d.a * (kd + dv), den);
    }
    const vus = [res];
    return c.filter((x) => {
        if (vus.some((v) => memeValeur(v, x.val))) return false;
        vus.push(x.val);
        return true;
    }).map((x) => ({ ...x, texte: `$${resTex(x.val)}$` }));
};

/** Une variante par famille (Vrai/Faux). */
export const candidatsParFamille = (it) => {
    const parCode = new Map();
    for (const x of shuffle(candidatsQuelconque(it))) if (!parCode.has(x.code)) parCode.set(x.code, x);
    return [...parCode.values()];
};

/**
 * QCM quelconque : bas_additionnes toujours, puis deux familles distinctes
 * parmi les autres (grand_denominateur et hauts_et_bas_multiplies comprises :
 * elles n'ont pas le dénominateur commun, c'est leur signature), à valeurs
 * distinctes. null si moins de deux familles.
 */
export const distracteursQcmQuelconque = (it) => {
    const cands = candidatsQuelconque(it);
    const fixe = cands.find((x) => x.code === 'bas_additionnes');
    if (!fixe) return null;
    const autres = shuffle(cands.filter((x) => x.code !== 'bas_additionnes'));
    for (let i = 0; i < autres.length; i++) {
        for (let j = i + 1; j < autres.length; j++) {
            if (autres[i].code !== autres[j].code && !memeValeur(autres[i].val, autres[j].val)) return [fixe, autres[i], autres[j]];
        }
    }
    return null;
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code de 1054 ou de 1097 ; hauts_et_bas_multiplies et coller sont des codes neufs. */
const CODE_COMMUN = {
    bas_additionnes: 'bas_additionnes', bas_soustraits: 'bas_additionnes',
    pas_converti: 'pas_converti', entier_pas_converti: 'pas_converti', grand_denominateur: 'pas_converti', petit_denominateur: 'pas_converti',
    voisin_meme_table: 'fait_de_table_voisin', hauts_et_bas_multiplies: 'hauts_et_bas_multiplies', coller: 'entier_colle_au_numerateur',
};

/** Toutes les familles d'un item, sans dédoublonnage (1054, 1097 et reconnaisseur de l'atelier) : [code, numérateur, dénominateur]. */
const familles = (it) => {
    const { g, d, op, den: L } = it;
    const s = op === '+' ? 1 : -1;
    const haut = g.a + s * d.a;
    if (it.cas === 'entier') {
        return [['entier_pas_converti', haut, d.b], ['bas_additionnes', haut, 1 + d.b],
            ...(s > 0 ? [['coller', Number(`${g.a}${d.a}`), d.b]] : []),
            ...[-1, 1].map((dv) => ['voisin_meme_table', g.a * d.b + dv + s * d.a, d.b])];
    }
    const kg = L / g.b;
    const kd = L / d.b;
    const f = [['bas_additionnes', haut, g.b + d.b], ['pas_converti', haut, L], ['pas_converti', g.a * kg + s * d.a, L], ['pas_converti', g.a + s * d.a * kd, L]];
    if (s < 0 && g.b !== d.b) f.push(['bas_soustraits', g.a - d.a, g.b - d.b], ['bas_soustraits', g.a - d.a, Math.abs(g.b - d.b)]);
    if (Math.max(g.b, d.b) !== L) f.push(['grand_denominateur', haut, Math.max(g.b, d.b)]);
    if (Math.min(g.b, d.b) !== L) f.push(['petit_denominateur', haut, Math.min(g.b, d.b)]);
    if (s > 0) f.push(['hauts_et_bas_multiplies', g.a * d.a, g.b * d.b]);
    [-1, 1].forEach((dv) => {
        if (it.cas === 'meme_den') f.push(['voisin_meme_table', haut + dv, L]);
        if (kg > 1) f.push(['voisin_meme_table', g.a * (kg + dv) + s * d.a * kd, L]);
        if (kd > 1) f.push(['voisin_meme_table', g.a * kg + s * d.a * (kd + dv), L]);
    });
    return f;
};

/** Le code commun d'une fraction fausse de la famille `code`, si aucune autre famille n'écrit la même fraction sous un autre code. */
const codeSur = (it, code, val) => {
    const c = CODE_COMMUN[code];
    return c && familles(it).every(([k, n, dd]) => n !== val.num || dd !== val.den || CODE_COMMUN[k] === c) ? c : null;
};

/** Le Vrai/Faux : chaque égalité fausse porte le code de sa famille, lu sur la fraction écrite à droite. */
const erreursVf = (items, affs, verites) => affs.map((a, i) => {
    const m = !verites[i] && a.code && a.ligne.match(/\\dfrac\{(\d+)\}\{(\d+)\}\$$/);
    return { code: m && codeSur(items[i], a.code, { num: Number(m[1]), den: Number(m[2]) }), ligne: a.ligne };
}).filter((e) => e.code);

/** Les deux cases d'une fraction de la série : une valeur n'est rangée que si une seule famille la donne (écritures justes non réduites comprises). */
const erreursCases = (it, trou) => {
    const L = it.den;
    const justes = [2, 3, 4, 5, 6].map((m) => [null, m * it.res.num, m * L]);
    const fams = familles(it).map(([k, n, dd]) => [CODE_COMMUN[k], n, dd]).concat(justes);
    return [[1, it.res.num], [2, L]].flatMap(([i, juste]) => {
        const vus = new Set();
        return fams.filter((f) => {
            const v = f[i];
            if (v === juste || v === 0 || (i === 1 && v < 0) || vus.has(v)) return false;
            vus.add(v);
            return f[0] && fams.every((h) => h[i] !== v || h[0] === f[0]);
        }).map((f) => ({ code: f[0], trou: trou + i - 1, valeur: String(f[i]) }));
    });
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = '<p><b>Calcule.</b> Écris le résultat en une seule fraction.</p>';

const payloadFlash = (items) => {
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, bas_additionnes: EXPLICATIONS.bas_additionnes, grand_denominateur: EXPLICATIONS.grand_denominateur } };
    payload._meta.params = { items };
    payload.contenu = { ...payload.contenu, erreurs: items.flatMap((it, k) => erreursCases(it, 2 * k)) };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = [
            tirerItem('multiples'),
            tirerItem('premiers'),
            tirerQuelconque({ opForce: '+' }),
            tirerQuelconque({ opForce: '-' }),
        ];
        if (items.some((it) => !it)) continue;
        if (new Set(items.map((it) => it.den)).size < 4) continue;
        return payloadFlash(shuffle(items));
    }
    return payloadFlash([REPLIS.multiples, REPLIS.premiers, REPLIS.quelconque_plus, REPLIS.quelconque_moins]);
};

// ─── Carte 2, format A : QCM, quatre choix ────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = randomChoice(['quelconque', 'quelconque', 'multiples', 'premiers']);
        const it = tirer(cas);
        if (!it) continue;
        const faux = cas === 'quelconque' ? distracteursQcmQuelconque(it) : distracteursQcm(it);
        if (!faux) continue;
        const bon = `$${resTex(it.res)}$`;
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>Quel est le résultat de ${tex(exprTex(it))} ?</b></p>`, opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: faux.map((f) => ({ code: codeSur(it, f.code, f.val), valeur: f.texte })).filter((e) => e.code) };
        return payload;
    }
    // Repli vérifié : 1/4 + 1/6 = 5/12 ; bas_additionnes 2/10, grand_denominateur 2/6, pas_converti 2/12.
    const it = REPLIS.quelconque_plus;
    const bon = `$${resTex(it.res)}$`;
    const payload = makeQcm([bon, `$${resTex({ num: 2, den: 10 })}$`, `$${resTex({ num: 2, den: 6 })}$`, `$${resTex({ num: 2, den: 12 })}$`], bon,
        `<p><b>Quel est le résultat de ${tex(exprTex(it))} ?</b></p>`, opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...it, distracteurs: [{ code: 'bas_additionnes' }, { code: 'grand_denominateur' }, { code: 'pas_converti' }] };
    const erreurs = [['bas_additionnes', 2, 10], ['grand_denominateur', 2, 6], ['pas_converti', 2, 12]]
        .map(([code, num, den]) => ({ code: codeSur(it, code, { num, den }), valeur: `$${resTex({ num, den })}$` })).filter((e) => e.code);
    payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 égalités, au moins une quelconque) ─

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cas = ['quelconque', ...shuffle([...CAS_5E]).slice(0, 2)];
        const items = cas.map((c) => tirer(c));
        if (items.some((it) => !it)) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const affs = items.map((it, i) => (it.cas === 'quelconque' ? affirmation(it, verites[i], candidatsParFamille(it)) : affirmation(it, verites[i])));
        if (affs.some((a) => !a)) continue;
        const lignes = shuffle(affs.map((a, i) => ({ ligne: a.ligne, vraie: verites[i], code: a.code })));
        if (new Set(lignes.map((l) => l.ligne)).size < 3) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites: lignes.map((l) => l.vraie), distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        payload.contenu = { ...payload.contenu, erreurs: erreursVf(items, affs, verites) };
        return payload;
    }
    // Repli vérifié : 5/6 − 1/4 = 7/12 vrai ; 2/7 + 3/7 = 5/14 faux (bas_additionnes) ; 4/7 − 2/21 = 2/21 faux (pas_converti).
    const items = [REPLIS.quelconque_moins, REPLIS.meme_den, REPLIS.multiples];
    const lignes = [
        tex(`${exprTex(REPLIS.quelconque_moins)} = ${resTex({ num: 7, den: 12 })}`),
        tex(`${exprTex(REPLIS.meme_den)} = ${resTex({ num: 5, den: 14 })}`),
        tex(`${exprTex(REPLIS.multiples)} = ${resTex({ num: 2, den: 21 })}`),
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [true, false, false], distracteurs: [{ code: 'bas_additionnes' }, { code: 'pas_converti' }] };
    payload.contenu = { ...payload.contenu, erreurs: erreursVf(items, lignes.map((ligne, i) => ({ ligne, code: [null, 'bas_additionnes', 'pas_converti'][i] })), [true, false, false]) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix : la clé, bas_additionnes toujours, puis deux familles distinctes ; chaque distracteur strictement positif, différent de la clé et des autres EN VALEUR ; collision → nouveau tirage',
        'cas de 5e : les familles et la sélection à rang pondéré de 1054 (importées) ; cas quelconque : familles ci-dessous',
        'le produit des dénominateurs (20/24 pour 10/12) n\'est jamais un distracteur : même valeur que la clé',
        'sacrifiés : les quatre exemples de 1054 (importés) et 5/6 avec 3/4',
    ],
    qcm_unique: {
        support: 'quelconque une fois sur deux (dénominateurs (4, 6), (6, 9), (4, 10), (6, 8), (8, 12), (6, 10)), sinon multiples ou premiers',
        familles_quelconque: {
            bas_additionnes: '(x ± y)/(b + d), la principale (Eduscol 5e 2025 q.27 : 3/7 + 3/7 → 6/14)',
            hauts_et_bas_multiplies: '(x × y)/(b × d), en addition (q.27 → 9/49)',
            pas_converti: 'le bon commun, un ou deux numérateurs non convertis',
            grand_denominateur: 'le plus grand dénominateur pris pour commun (5/6 + 3/4 → 8/6), neuf en 4e',
            voisin_meme_table: 'une conversion avec un fait de table voisin',
        },
    },
    vf_tableau_vf: 'trois égalités, une quelconque au moins et deux cas de 5e ; chaque fausse tire sa famille au hasard parmi les candidats de son item (règle de 1054, résidu structurel assumé)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    meme_qcm_unique: genQcm,
    meme_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 2, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'meme', label: L_MEME, etapes: 2, formatsList: [
        { key: 'meme_qcm_unique', label: 'QCM' },
        { key: 'meme_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    meme_qcm_unique: ['fait_de_table_voisin'],
    meme_tableau_vf: ['fait_de_table_voisin', 'entier_colle_au_numerateur'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
